import "./style.css";

import {
  listModels,
  type Backend,
  type ModelProgress,
  type ModelSummary,
} from "./model";
import {
  DEFAULT_GAME_ID,
  DEFAULT_VARIANT_ID,
  gameRegistry,
  type GameDefinition,
  type GameEnvironment,
  type GameRenderer,
  type Player,
} from "./game";
import type { IntuitionResult, SearchSnapshot, VisitAggregator } from "./search";
import {
  type ErrorChainEntry,
  isPrivateMemberAccessError,
  isSafariBrowser,
} from "./runtime-error";
import { nextRecoveryStep } from "./search/recovery";

const MAX_SIMULATIONS = 4096;

const app = document.querySelector<HTMLElement>("#app");
if (!app) throw new Error("Missing #app");

app.innerHTML = `
  <header class="site-header">
    <a class="wordmark" href="/" aria-label="nanoAlphaZero Playground home">
      <span>nanoAlphaZero Playground</span>
    </a>
    <div class="header-actions">
      <a
        class="repository-link"
        href="https://github.com/wtedw/nanoAlphaZero"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="nanoAlphaZero on GitHub (opens in a new tab)"
      >GitHub <span aria-hidden="true">↗</span></a>
    </div>
  </header>

  <section class="play-section" aria-label="Play a board game">
    <section id="game-setup" class="game-toolbar" aria-label="Game and model">
      <div class="toolbar-fields">
        <label>Game
          <select id="game-select" aria-label="Game"></select>
        </label>
        <label>Variant
          <select id="variant-select" aria-label="Variant"></select>
        </label>
        <label class="toolbar-model">Model
          <select id="model-select" aria-label="Model"></select>
        </label>
        <label>Play as
          <select id="human-color">
            <option value="0">P1 · White</option>
            <option value="1" selected>P2 · Black</option>
          </select>
        </label>
        <label class="strength-control toolbar-strength">Search effort
          <span><output id="setup-simulations-value">32</output> <span class="simulation-unit">simulations</span></span>
          <input id="setup-simulations" type="range" min="1" max="${MAX_SIMULATIONS}" step="1" value="32" aria-valuetext="32 simulations">
        </label>
        <button id="play-game" class="play-button">Play</button>
      </div>
      <section
        id="model-runtime"
        class="model-runtime"
        aria-live="polite"
        role="progressbar"
        aria-label="Model preparation"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow="0"
      >
        <div class="status-copy">
          <i class="model-power" aria-hidden="true"></i>
          <span id="phase-label">Loading catalog</span>
          <span id="progress-copy">Looking for compatible checkpoints…</span>
        </div>
      </section>
    </section>
    <div class="play-grid">
      <div class="board-column">
        <div class="board-heading">
          <span id="board-context">Chess · Standard 8×8</span>
          <div class="board-heading-actions">
            <label id="board-view-control" class="board-view-control">
              <span>Board view</span>
              <select id="board-view" aria-label="Board view">
                <option value="0">White on bottom</option>
                <option value="1" selected>Black on bottom</option>
              </select>
            </label>
            <button
              id="unbeatable-model"
              type="button"
              aria-haspopup="dialog"
              hidden
            >This model is unbeatable as P1</button>
          </div>
        </div>
        <div id="board" class="game-board" role="grid"></div>
        <section id="game-result" class="game-result" aria-live="polite">
          <span id="game-result-label" class="game-result-label">Ready to play</span>
          <strong id="game-result-text"></strong>
          <div id="recovery-actions" class="recovery-actions" hidden>
            <button id="retry-webgpu" type="button">Retry WebGPU</button>
            <button id="use-cpu-mode" type="button">Use CPU mode</button>
          </div>
          <div
            id="game-progress"
            class="game-progress"
            role="progressbar"
            aria-label="Model search progress"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow="0"
            hidden
          ><i id="game-progress-bar"></i></div>
        </section>
        <div class="board-tools">
          <button id="new-game">New game</button>
          <button id="undo" disabled>Undo</button>
          <button id="pass-move" class="pass-control" hidden>Pass</button>
        </div>
        <details class="position-tools pgn-control advanced-tool" hidden>
          <summary>Position tools</summary>
          <div class="position-tools-content">
            <label class="pgn-tool">PGN
              <textarea id="pgn-input" rows="5" aria-label="Chess PGN" placeholder="Paste a PGN game here"></textarea>
            </label>
            <button id="load-pgn">Load PGN</button>
          </div>
        </details>
        <div class="board-moves">
          <div class="game-record-heading">
            <p id="game-record-label" class="control-label">PGN</p>
            <button id="copy-game-record" type="button">Copy PGN</button>
          </div>
          <textarea
            id="game-record"
            class="game-record"
            rows="5"
            aria-label="Generated PGN"
            readonly
          >*</textarea>
          <span id="copy-game-record-status" class="copy-game-record-status" aria-live="polite"></span>
          <ol id="move-list" hidden><li>Game ready</li></ol>
        </div>
      </div>
      <div class="analysis-column">
        <div class="analysis-actions">
          <div class="search-actions">
            <button id="analyze-position">Search position</button>
            <button id="cancel-search" disabled>Abort search</button>
          </div>
          <button
            id="self-play-mode"
            class="self-play-button"
            type="button"
            aria-pressed="false"
          >Self-play mode</button>
        </div>
        <aside class="game-controls">
          <section id="game-live" class="game-live">
          <section class="model-section model-outlook" aria-labelledby="position-outlook-heading">
            <div class="subheading">
              <h4 id="position-outlook-heading">Position outlook</h4>
              <span class="player-to-move-label">P1 · White</span>
            </div>
            <div class="scalar-readout">
              <span>Network value</span><strong id="value">—</strong><small>P(win) − P(loss), player to move</small>
            </div>
            <div class="thinking-wdl">
              <div class="subheading"><h4>WDL outlook</h4><span class="player-to-move-label">P1 · White</span></div>
              <div id="wdl" class="wdl">
                ${["Win", "Loss", "No result"]
                  .map(
                    (label) => `
                      <div class="wdl-row">
                        <span>${label}</span>
                        <div class="wdl-track"><i style="width:0%"></i></div>
                        <strong>—</strong>
                      </div>`,
                  )
                  .join("")}
              </div>
              <p id="wdl-logits" class="mono-note">Raw logits —</p>
            </div>
          </section>
          <section class="policy-analysis" aria-labelledby="policy-heading">
            <div class="policy-heading">
              <div>
                <h4 id="policy-heading">Policy</h4>
                <span>network policy with live search updates</span>
              </div>
            </div>
            <div class="advanced-search-row advanced-tool" hidden>
              <span id="search-config-summary">OMCTX · max visits</span>
              <button id="open-search-config" type="button">Advanced</button>
            </div>
            <dl class="search-telemetry">
              <div><dt>Search</dt><dd id="search-state">Idle</dd></div>
              <div><dt>Time</dt><dd id="search-time">—</dd></div>
              <div><dt>PV</dt><dd id="search-pv">—</dd></div>
              <div><dt>MCTS value</dt><dd id="mcts-root-value">—</dd></div>
            </dl>
            <div class="policy-header">
              <span>Move</span><span>Model logit</span><span>Prior</span><span>Search π′</span><span>Q</span>
            </div>
            <div id="policy-list" class="policy-list">
              <p class="empty-copy">Policy appears when play begins.</p>
            </div>
          </section>
          </section>
        </aside>
      </div>
    </div>
  </section>

  <dialog
    id="unbeatable-dialog"
    class="unbeatable-dialog"
    aria-labelledby="unbeatable-dialog-title"
  >
    <div class="unbeatable-dialog-card">
      <div class="unbeatable-dialog-header">
        <button
          id="close-unbeatable-dialog"
          type="button"
          aria-label="Close explanation"
        >×</button>
      </div>
      <h2 id="unbeatable-dialog-title">Regarding “Unbeatable”</h2>
      <div class="unbeatable-dialog-copy">
        <p>
          AlphaZero-style models optimize for the final game outcome rather
          than for winning as quickly as possible or delaying an inevitable
          loss for as long as possible.
        </p>
        <p>
          As a result, once the model recognizes that a position is a forced
          loss, every move that still leads to a loss can appear equivalent.
          Likewise, in a forced-win position, it has no inherent preference
          for the fastest victory. It may therefore choose moves that seem
          unnecessarily slow or “torturous” to a human player. You can see
          this type of behavior by playing 4×4 Hex as P2.
        </p>
        <p>
          The opposite behavior can be seen when playing as P1 versus the
          Connect Four model. If you open in the center column, the model
          recognizes that you can force a win by playing optimally. The model
          may then appear to “give up” because delaying the loss offers no
          additional reward.
        </p>
      </div>
    </div>
  </dialog>

  <span id="advanced-tools-status" class="visually-hidden" aria-live="polite"></span>

  <dialog
    id="search-config-dialog"
    class="search-config-dialog"
    aria-labelledby="search-config-dialog-title"
  >
    <form id="search-config-form" class="search-config-card">
      <button id="close-search-config" class="dialog-close" type="button" aria-label="Close advanced search settings">×</button>
      <h2 id="search-config-dialog-title">Advanced search</h2>
      <p>Edit the OMCTX configuration used by both model turns and position analysis.</p>
      <label for="search-config-json">MCTS config</label>
      <textarea id="search-config-json" rows="10" spellcheck="false"></textarea>
      <p id="search-config-error" class="search-config-error" role="alert" hidden></p>
      <div class="search-config-actions">
        <button id="reset-search-config" type="button">Reset defaults</button>
        <button type="submit">Apply config</button>
      </div>
    </form>
  </dialog>

`;

const modelRuntime = document.querySelector<HTMLElement>("#model-runtime")!;
const phaseLabel = document.querySelector<HTMLElement>("#phase-label")!;
const progressCopy = document.querySelector<HTMLElement>("#progress-copy")!;
const gameSelect = document.querySelector<HTMLSelectElement>("#game-select")!;
const variantSelect = document.querySelector<HTMLSelectElement>("#variant-select")!;
const modelSelect = document.querySelector<HTMLSelectElement>("#model-select")!;
let selectedGameId = DEFAULT_GAME_ID;
let selectedVariantId = DEFAULT_VARIANT_ID;
let selectedDefinition: GameDefinition<unknown> =
  gameRegistry.get(selectedGameId, selectedVariantId);
let selectedModelId = "";
let availableModels: ModelSummary[] = [];
const modelDownloadBytes = new Map<string, number>();
const confirmedModelDownloads = new Set<string>();

function formatBytes(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MiB`
    : `${Math.round(bytes / 1024)} KiB`;
}

function formatMs(value: number | null): string {
  if (value === null) return "cached";
  if (value >= 1000) return `${(value / 1000).toFixed(2)} s`;
  return `${value.toFixed(1)} ms`;
}

function modelDownloadWarning(modelId: string): string {
  const summary = modelDownloadBytes.get(modelId);
  const size = summary === undefined ? "the model's weight shards" : formatBytes(summary);
  const cached = "If this model is already cached, no network download is needed.\n\n";
  return `${cached}Loading ${modelSelect.selectedOptions[0]?.textContent ?? modelId} may download up to ${size} of model weights. Continue?`;
}

function confirmModelDownload(): boolean {
  if (!selectedModelId || confirmedModelDownloads.has(selectedModelId)) return true;
  const accepted = window.confirm(modelDownloadWarning(selectedModelId));
  if (accepted) confirmedModelDownloads.add(selectedModelId);
  return accepted;
}

function setProgressRatio(ratio: number): void {
  const percentage = Math.min(100, Math.max(0, ratio * 100));
  modelRuntime.style.setProperty("--model-progress", `${percentage}%`);
  modelRuntime.setAttribute("aria-valuenow", String(Math.round(percentage)));
}

function cacheProgressCopy(progress: ModelProgress): string {
  const bytes = progress.totalBytes > 0
    ? `${formatBytes(progress.loadedBytes)} / ${formatBytes(progress.totalBytes)}`
    : "";
  if (progress.phase === "manifest") {
    return "Reading the checkpoint manifest and checking local storage…";
  }
  if (progress.phase === "verify") {
    return `Checking SHA-256 integrity${bytes ? ` · ${bytes}` : ""}`;
  }
  if (progress.phase === "hydrate") {
    return `Moving parameters onto this device${bytes ? ` · ${bytes}` : ""}`;
  }
  if (progress.phase === "ready") {
    if (progress.cache === "hit") return "Loaded entirely from the local browser cache.";
    if (progress.cache === "miss") return "Weights stored locally for your next visit.";
    if (progress.cache === "mixed") return "Cached weights reused; missing files were refreshed.";
    if (progress.cache === "unavailable") return "Ready for this session · local cache unavailable.";
    return "The model is ready in this browser.";
  }
  if (progress.cache === "hit") {
    return `${bytes} · no network download needed`;
  }
  if (progress.cache === "mixed") {
    return `${bytes} · downloading only missing files`;
  }
  if (progress.cache === "unavailable") {
    return `${bytes} · local cache unavailable`;
  }
  return `${bytes} · saved locally for later visits`;
}

function setProgress(progress: ModelProgress): void {
  const labels: Record<ModelProgress["phase"], string> = {
    manifest: "Checking model and cache",
    download: progress.cache === "hit"
      ? "Loading from local cache"
      : progress.cache === "mixed"
      ? "Reusing cached weights"
      : "Downloading model weights",
    verify: "Verifying model weights",
    hydrate: "Preparing model for this device",
    ready: "Model ready",
  };
  modelRuntime.dataset.phase = progress.phase;
  modelRuntime.classList.toggle("is-active", progress.phase !== "ready");
  if (progress.backend) activeBackend = progress.backend;
  phaseLabel.textContent = progress.phase === "ready" && activeBackend
    ? `Model ready · ${backendLabel(activeBackend)}`
    : labels[progress.phase];
  const ratio =
    progress.totalBytes > 0 ? progress.loadedBytes / progress.totalBytes : 0;
  setProgressRatio(progress.phase === "ready" ? 1 : ratio);
  progressCopy.textContent = cacheProgressCopy(progress);
}

function setModelRuntimeMessage(
  phase: string,
  message: string,
  options: {
    active?: boolean;
    progress?: number;
    state?: "idle" | "pending" | "ready";
  } = {},
): void {
  modelRuntime.dataset.phase =
    options.state ?? (options.active ? "pending" : "idle");
  modelRuntime.classList.toggle("is-active", options.active ?? false);
  phaseLabel.textContent = phase;
  progressCopy.textContent = message;
  setProgressRatio(options.progress ?? 0);
}

function renderWdl(probabilities: Float32Array, logits?: Float32Array): void {
  const rows = document.querySelectorAll<HTMLElement>(".wdl-row");
  probabilities.forEach((probability, index) => {
    const row = rows[index];
    if (!row) return;
    const bar = row.querySelector<HTMLElement>("i");
    const value = row.querySelector<HTMLElement>("strong");
    if (bar) bar.style.width = `${probability * 100}%`;
    if (value) value.textContent = `${(probability * 100).toFixed(1)}%`;
  });
  if (logits) {
    document.querySelector("#wdl-logits")!.textContent =
      `Raw logits  [${Array.from(logits, (value) => value.toFixed(6)).join(", ")}]`;
  }
}

function renderCandidateRows(
  candidates: Array<{
    action: number;
    logit: number;
    prior: number;
    actionWeight: number;
    q: number | null;
  }>,
): void {
  document.querySelector("#policy-list")!.innerHTML = candidates
    .slice(0, 10)
    .map((candidate, index) => `
      <div class="policy-row">
        <span><i>${String(index + 1).padStart(2, "0")}</i>${environment.formatAction(state, candidate.action)}</span>
        <span>${candidate.logit.toFixed(4)}</span>
        <span>${(candidate.prior * 100).toFixed(1)}%</span>
        <strong>${(candidate.actionWeight * 100).toFixed(1)}%</strong>
        <span>${candidate.q === null ? "—" : candidate.q.toFixed(3)}</span>
      </div>`)
    .join("");
}

function renderModelIntuition(result: {
  rootValue: number;
  rootWdlLogits?: Float32Array;
  rootWdlProbabilities?: Float32Array;
  candidates: Array<{ action: number; logit: number; prior: number }>;
}): void {
  document.querySelector("#value")!.textContent = result.rootValue.toFixed(6);
  if (result.rootWdlProbabilities) renderWdl(result.rootWdlProbabilities, result.rootWdlLogits);
  document.querySelector("#policy-list")!.innerHTML = [...result.candidates]
    .sort((left, right) => right.prior - left.prior || left.action - right.action)
    .slice(0, 10)
    .map((candidate, index) => `
      <div class="policy-row">
        <span><i>${String(index + 1).padStart(2, "0")}</i>${environment.formatAction(state, candidate.action)}</span>
        <span>${candidate.logit.toFixed(4)}</span>
        <span>${(candidate.prior * 100).toFixed(1)}%</span>
        <strong>—</strong>
        <span>—</span>
      </div>`)
    .join("");
}

function renderSearchThinking(result: {
  mctsRootValue: number;
  candidates: Array<{
    action: number;
    logit: number;
    prior: number;
    visits: number;
    actionWeight: number;
    q: number;
  }>;
}): void {
  document.querySelector("#mcts-root-value")!.textContent = result.mctsRootValue.toFixed(6);
  renderCandidateRows(result.candidates);
}

const boardElement = document.querySelector<HTMLElement>("#board")!;
const humanPlayerElement = document.querySelector<HTMLSelectElement>("#human-color")!;
const selfPlayButton = document.querySelector<HTMLButtonElement>("#self-play-mode")!;
const playButton = document.querySelector<HTMLButtonElement>("#play-game")!;
const setupSimulations = document.querySelector<HTMLInputElement>("#setup-simulations")!;

function simulationCount(input: HTMLInputElement): number {
  const requested = Number(input.value);
  if (requested <= 1) return 1;
  return Math.min(MAX_SIMULATIONS, Math.max(8, Math.round(requested / 8) * 8));
}

function renderSimulationCount(
  input: HTMLInputElement,
  output: HTMLOutputElement,
): void {
  const simulations = simulationCount(input);
  input.value = String(simulations);
  output.value = String(simulations);
  const unit = output.nextElementSibling;
  if (unit?.classList.contains("simulation-unit")) {
    unit.textContent = simulations === 1 ? "simulation" : "simulations";
  }
  input.setAttribute("aria-valuetext", `${simulations} simulation${simulations === 1 ? "" : "s"}`);
}

function adjustSimulationWithKeyboard(event: KeyboardEvent): void {
  if (!["ArrowLeft", "ArrowDown", "ArrowRight", "ArrowUp", "Home", "End"].includes(event.key)) {
    return;
  }
  event.preventDefault();
  const input = event.currentTarget as HTMLInputElement;
  const current = simulationCount(input);
  const next = event.key === "Home" ? 1
    : event.key === "End" ? MAX_SIMULATIONS
    : event.key === "ArrowLeft" || event.key === "ArrowDown"
    ? (current <= 8 ? 1 : current - 8)
    : (current === 1 ? 8 : Math.min(MAX_SIMULATIONS, current + 8));
  input.value = String(next);
  input.dispatchEvent(new Event("input", { bubbles: true }));
}
const searchConfigDialog =
  document.querySelector<HTMLDialogElement>("#search-config-dialog")!;
const searchConfigJson =
  document.querySelector<HTMLTextAreaElement>("#search-config-json")!;
const searchConfigError =
  document.querySelector<HTMLElement>("#search-config-error")!;
const searchConfigSummary =
  document.querySelector<HTMLElement>("#search-config-summary")!;
const boardColumn = document.querySelector<HTMLElement>(".board-column")!;
const boardContext = document.querySelector<HTMLElement>("#board-context")!;
const boardViewControl =
  document.querySelector<HTMLElement>("#board-view-control")!;
const boardViewElement =
  document.querySelector<HTMLSelectElement>("#board-view")!;
const unbeatableModel =
  document.querySelector<HTMLButtonElement>("#unbeatable-model")!;
const unbeatableDialog =
  document.querySelector<HTMLDialogElement>("#unbeatable-dialog")!;
const closeUnbeatableDialog =
  document.querySelector<HTMLButtonElement>("#close-unbeatable-dialog")!;
const gameResult = document.querySelector<HTMLElement>("#game-result")!;
const gameResultLabel = document.querySelector<HTMLElement>("#game-result-label")!;
const gameResultText = document.querySelector<HTMLElement>("#game-result-text")!;
const recoveryActions = document.querySelector<HTMLElement>("#recovery-actions")!;
const retryWebgpuButton = document.querySelector<HTMLButtonElement>("#retry-webgpu")!;
const useCpuModeButton = document.querySelector<HTMLButtonElement>("#use-cpu-mode")!;
const gameProgress = document.querySelector<HTMLElement>("#game-progress")!;
const gameProgressBar = document.querySelector<HTMLElement>("#game-progress-bar")!;
const undoButton = document.querySelector<HTMLButtonElement>("#undo")!;
const passButton = document.querySelector<HTMLButtonElement>("#pass-move")!;
const analyzeButton = document.querySelector<HTMLButtonElement>("#analyze-position")!;
const cancelButton = document.querySelector<HTMLButtonElement>("#cancel-search")!;
const gameRecordLabel = document.querySelector<HTMLElement>("#game-record-label")!;
const gameRecord = document.querySelector<HTMLTextAreaElement>("#game-record")!;
const copyGameRecordButton =
  document.querySelector<HTMLButtonElement>("#copy-game-record")!;
const copyGameRecordStatus =
  document.querySelector<HTMLElement>("#copy-game-record-status")!;
let environment: GameEnvironment<unknown> = selectedDefinition.createEnvironment();
let advancedToolsVisible = false;

function renderAdvancedTools(): void {
  document.querySelectorAll<HTMLElement>(".pgn-control").forEach((element) => {
    element.hidden = !advancedToolsVisible || !selectedDefinition.controls.pgn;
  });
  document.querySelectorAll<HTMLElement>(".advanced-search-row").forEach((element) => {
    element.hidden = !advancedToolsVisible;
  });
  if (!advancedToolsVisible && searchConfigDialog.open) searchConfigDialog.close();
}

document.addEventListener("keydown", (event) => {
  if (
    event.code !== "KeyA" || !event.altKey || !event.shiftKey ||
    event.ctrlKey || event.metaKey
  ) return;
  const target = event.target;
  if (
    target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  ) return;
  event.preventDefault();
  advancedToolsVisible = !advancedToolsVisible;
  renderAdvancedTools();
  document.querySelector<HTMLElement>("#advanced-tools-status")!.textContent =
    advancedToolsVisible ? "Advanced tools enabled" : "Advanced tools hidden";
});

interface BrowserMctsConfig {
  mcts_visit_exponent: number;
  mcts_maxvisit_init: number;
  mcts_value_scale: number;
  mcts_visit_aggregator: VisitAggregator;
}

const DEFAULT_MCTS_CONFIG: BrowserMctsConfig = {
  mcts_visit_exponent: 0.5,
  mcts_maxvisit_init: 50,
  mcts_value_scale: 1,
  mcts_visit_aggregator: "max",
};
let mctsConfig: BrowserMctsConfig = { ...DEFAULT_MCTS_CONFIG };

function formatMctsConfig(config: BrowserMctsConfig): string {
  return JSON.stringify(config, null, 2);
}

function parseMctsConfig(text: string): BrowserMctsConfig {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch (error) {
    throw new RangeError(error instanceof Error ? error.message : "Invalid JSON");
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new RangeError("MCTS config must be a JSON object");
  }
  const record = value as Record<string, unknown>;
  const allowed = new Set([
    "mcts_visit_exponent",
    "mcts_maxvisit_init",
    "mcts_value_scale",
    "mcts_visit_aggregator",
  ]);
  const unknown = Object.keys(record).filter((key) => !allowed.has(key));
  if (unknown.length > 0) throw new RangeError(`Unknown MCTS setting: ${unknown.join(", ")}`);
  const numberSetting = (key: keyof BrowserMctsConfig): number => {
    const setting = record[key];
    if (typeof setting !== "number" || !Number.isFinite(setting) || setting < 0) {
      throw new RangeError(`${key} must be a non-negative finite number`);
    }
    return setting;
  };
  const aggregator = record.mcts_visit_aggregator;
  if (!(["max", "sum", "mean", "log_sum"] as unknown[]).includes(aggregator)) {
    throw new RangeError("mcts_visit_aggregator must be max, sum, mean, or log_sum");
  }
  return {
    mcts_visit_exponent: numberSetting("mcts_visit_exponent"),
    mcts_maxvisit_init: numberSetting("mcts_maxvisit_init"),
    mcts_value_scale: numberSetting("mcts_value_scale"),
    mcts_visit_aggregator: aggregator as VisitAggregator,
  };
}

function openSearchConfig(): void {
  searchConfigJson.value = formatMctsConfig(mctsConfig);
  searchConfigError.hidden = true;
  searchConfigDialog.showModal();
}

document.querySelector("#open-search-config")!.addEventListener("click", openSearchConfig);
document.querySelector("#close-search-config")!.addEventListener("click", () => searchConfigDialog.close());
document.querySelector("#reset-search-config")!.addEventListener("click", () => {
  searchConfigJson.value = formatMctsConfig(DEFAULT_MCTS_CONFIG);
  searchConfigError.hidden = true;
});
document.querySelector("#search-config-form")!.addEventListener("submit", (event) => {
  event.preventDefault();
  try {
    mctsConfig = parseMctsConfig(searchConfigJson.value);
    searchConfigSummary.textContent =
      `Custom OMCTX · ${mctsConfig.mcts_visit_aggregator} visits`;
    searchConfigError.hidden = true;
    searchConfigDialog.close();
  } catch (error) {
    searchConfigError.textContent = error instanceof Error ? error.message : "Invalid MCTS config";
    searchConfigError.hidden = false;
  }
});
searchConfigDialog.addEventListener("click", (event) => {
  if (event.target === searchConfigDialog) searchConfigDialog.close();
});

unbeatableModel.addEventListener("click", () => {
  unbeatableDialog.showModal();
});
closeUnbeatableDialog.addEventListener("click", () => {
  unbeatableDialog.close();
});
unbeatableDialog.addEventListener("click", (event) => {
  if (event.target === unbeatableDialog) unbeatableDialog.close();
});
let renderer: GameRenderer<unknown> = selectedDefinition.createRenderer();
renderer.mount(boardElement);
let state: unknown = environment.init();
let states: unknown[] = [state];
let humanPlayer: Player = 1;
let viewPlayer: Player = 1;
let selfPlayMode = false;
let activeBackend: Backend | null = null;
let lastModelAction: { action: number; player: Player } | null = null;
let requestId = 0;
let searching = false;
let searchPurpose: "move" | "analyze" | null = null;
let intuitionPending = false;
let gameStarted = false;
let lastMove: {
  action: number;
  player: Player;
  label: string;
  source: "human" | "model";
} | null = null;
let gameNotice: { label: string; text: string } | null = null;
let activeSearchProgress: { completed: number; total: number } | null = null;
let pendingSearchSnapshot: SearchSnapshot | null = null;
let searchRenderFrame: number | null = null;
let searchHistory: SearchSnapshot[] = [];
type WorkPayload =
  | {
      type: "intuition";
      gameId: string;
      variantId: string;
      state: unknown;
      modelId: string;
    }
  | {
      type: "search";
      gameId: string;
      variantId: string;
      state: unknown;
      modelId: string;
      simulations: number;
      mctsConfig: {
        visitExponent: number;
        maxVisitInit: number;
        valueScale: number;
        visitAggregator: VisitAggregator;
      };
    };

interface SearchWorkerMessage {
  type: string;
  requestId: number;
  snapshot?: SearchSnapshot;
  progress?: ModelProgress;
  intuition?: IntuitionResult;
  result?: {
    action: number;
    rootValue: number;
    mctsRootValue: number;
    rootWdlLogits?: Float32Array;
    rootWdlProbabilities?: Float32Array;
    nodes: number;
    elapsedMs: number;
    principalVariation: number[];
    candidates: Array<{
      action: number;
      visits: number;
      actionWeight: number;
      logit: number;
      prior: number;
      q: number;
    }>;
  };
  message?: string;
  name?: string;
  modelReady?: boolean;
  causeChain?: ErrorChainEntry[];
}

const faultParameters = new URLSearchParams(window.location.search);
const faultKind = faultParameters.get("e2e-fault");
const parsedFaultCount = Number.parseInt(
  faultParameters.get("e2e-fault-count") ?? "0",
  10,
);
let e2eFaultBudget = faultKind === "private-member" &&
    Number.isFinite(parsedFaultCount)
  ? Math.max(0, parsedFaultCount)
  : 0;
let lastWorkPayload: WorkPayload | null = null;
let recoveryAttempt = 0;
let recoveryTimer: ReturnType<typeof setTimeout> | null = null;
let compatibilityBackend: "wasm" | null = null;
let awaitingRecoveryChoice = false;
let workerGeneration = 0;
const recoverySessionId = crypto.randomUUID?.() ??
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
const recoveryTimeline: Array<Record<string, unknown>> = [];

function recoveryLog(
  event: string,
  details: Record<string, unknown> = {},
): void {
  const entry = {
    session: recoverySessionId,
    elapsedMs: Math.round(performance.now()),
    event,
    ...details,
  };
  recoveryTimeline.push(entry);
  console.info(`[nanoAlphaZero WebGPU] ${JSON.stringify(entry)}`);
}

Object.assign(window, {
  nanoAlphaZeroRecoveryReport: () => JSON.stringify(recoveryTimeline, null, 2),
});

function createSearchWorker(): Worker {
  workerGeneration += 1;
  const worker = new Worker(new URL("./search/worker.ts", import.meta.url), {
    type: "module",
  });
  worker.addEventListener("message", handleWorkerMessage);
  recoveryLog("worker-created", { workerGeneration });
  return worker;
}

function recreateSearchWorker(reason: string): void {
  recoveryLog("worker-terminating", { workerGeneration, reason });
  searchWorker.terminate();
  searchWorker = createSearchWorker();
}

let searchWorker = createSearchWorker();
recoveryLog("session-start", {
  userAgent: navigator.userAgent,
  safari: isSafariBrowser(navigator.userAgent),
  injectedFaults: e2eFaultBudget,
});
console.info(
  "[nanoAlphaZero WebGPU] To copy the main-thread report, run: " +
    "copy(nanoAlphaZeroRecoveryReport())",
);

function shortPlayerName(player: Player): string {
  return selectedDefinition.playerNames[player].split(" · ")[0]!;
}

function playerLabel(player: Player): string {
  return `P${player + 1} · ${selectedDefinition.playerNames[player]}`;
}

function isHumanTurn(player = environment.currentPlayer(state)): boolean {
  return !selfPlayMode && player === humanPlayer;
}

function isModelTurn(player = environment.currentPlayer(state)): boolean {
  return selfPlayMode || player !== humanPlayer;
}

function renderSelfPlayMode(): void {
  selfPlayButton.setAttribute("aria-pressed", String(selfPlayMode));
  selfPlayButton.classList.toggle("is-active", selfPlayMode);
  humanPlayerElement.disabled = selfPlayMode;
}

function playerTone(player: Player): string {
  const tones: Record<string, readonly [string, string]> = {
    chess: ["white", "black"],
    "connect-four": ["red", "yellow"],
    hex: ["red", "blue"],
    go: ["black", "white"],
  };
  return tones[selectedGameId]?.[player] ?? "neutral";
}

function renderUnbeatableNotice(): void {
  const isDryVortexV7 = selectedModelId === "dry-vortex-25-v7";
  const isProbablyUnbeatable =
    selectedVariantId === "go_5x5" || isDryVortexV7;
  unbeatableModel.textContent = isProbablyUnbeatable
    ? "THIS MODEL IS PROBABLY UNBEATABLE AS P1"
    : "This model is unbeatable as P1";
  const supportsNotice = isDryVortexV7 ||
    selectedGameId === "hex" || selectedGameId === "go";
  unbeatableModel.hidden = (!selfPlayMode && humanPlayer !== 1) || !supportsNotice;
}

function renderGameBanner(): void {
  const terminal = gameStarted && environment.isTerminal(state);
  const currentPlayer = environment.currentPlayer(state);
  let label = "Ready to play";
  let text = "Choose settings, then press Play.";
  let tone = "neutral";
  let progress: { completed: number; total: number } | null = null;

  if (terminal) {
    label = "Game complete";
    text = selectedDefinition.statusText(state);
    tone = lastMove ? playerTone(lastMove.player) : "neutral";
  } else if (gameStarted && searching && activeSearchProgress) {
    label = searchPurpose === "analyze"
      ? "Analyzing position"
      : "Model is thinking";
    text = `${shortPlayerName(currentPlayer)} · ${activeSearchProgress.completed} of ${activeSearchProgress.total} simulations`;
    tone = playerTone(currentPlayer);
    progress = activeSearchProgress;
  } else if (gameNotice) {
    label = gameNotice.label;
    text = gameNotice.text;
  } else if (
    gameStarted &&
    lastMove?.source === "model" &&
    isHumanTurn(currentPlayer)
  ) {
    label = "Model played";
    text = lastMove.label;
    tone = playerTone(lastMove.player);
  } else if (gameStarted) {
    label = selfPlayMode
      ? "Self-play"
      : isHumanTurn(currentPlayer) ? "Your turn" : "Model turn";
    text = selectedDefinition.statusText(state);
    tone = playerTone(currentPlayer);
  }

  gameResult.dataset.tone = tone;
  gameResult.classList.toggle("is-searching", progress !== null);
  gameResult.classList.toggle("is-terminal", terminal);
  gameResultLabel.textContent = label;
  gameResultText.textContent = text;
  recoveryActions.hidden = !awaitingRecoveryChoice;
  gameProgress.hidden = progress === null;
  const percent = progress
    ? Math.min(100, Math.max(0, progress.completed / progress.total * 100))
    : 0;
  gameProgressBar.style.width = `${percent}%`;
  gameProgress.setAttribute("aria-valuenow", String(Math.round(percent)));
  gameProgress.setAttribute(
    "aria-valuetext",
    progress ? `${progress.completed} of ${progress.total} simulations` : "Idle",
  );
}

function renderBoard(): void {
  const terminal = environment.isTerminal(state);
  const currentPlayer = environment.currentPlayer(state);
  for (const label of document.querySelectorAll<HTMLElement>(
    ".player-to-move-label",
  )) {
    label.textContent = playerLabel(currentPlayer);
  }
  renderUnbeatableNotice();
  renderer.render({
    state,
    humanPlayer,
    viewPlayer,
    interactive: gameStarted && !searching && !terminal && isHumanTurn(currentPlayer),
    lastModelAction,
    onAction: commitMove,
  });
  renderGameBanner();
  undoButton.disabled = !gameStarted || states.length <= 1 || searching;
  analyzeButton.textContent = selfPlayMode
    ? searching ? "Model is thinking" : "Continue self-play"
    : isHumanTurn(currentPlayer)
    ? searching ? "Searching position" : "Search position"
    : searching
    ? "Bot is thinking"
    : "Continue bot turn";
  analyzeButton.disabled = !gameStarted || searching || terminal;
  cancelButton.disabled = !searching;
  const moveList = document.querySelector<HTMLOListElement>("#move-list")!;
  const moves = selectedDefinition.moveHistory(state);
  moveList.innerHTML = moves.length
    ? moves.map((move) => `<li>${move}</li>`).join("")
    : "<li>Game ready</li>";
  const isPgn = selectedGameId === "chess" && selectedDefinition.serializeGame;
  gameRecordLabel.textContent = isPgn ? "PGN" : "Move record";
  gameRecord.setAttribute("aria-label", isPgn ? "Generated PGN" : "Generated move record");
  copyGameRecordButton.textContent = isPgn ? "Copy PGN" : "Copy moves";
  const serialized = selectedDefinition.serializeGame
    ? selectedDefinition.serializeGame(states)
    : formatMoveRecord(moves);
  if (gameRecord.value !== serialized) copyGameRecordStatus.textContent = "";
  gameRecord.value = serialized;
}

function formatMoveRecord(moves: readonly string[]): string {
  if (moves.length === 0) return "No moves yet";
  const tokens: string[] = [];
  for (const [index, move] of moves.entries()) {
    if (index % 2 === 0) tokens.push(`${Math.floor(index / 2) + 1}.`);
    tokens.push(move);
  }
  return tokens.join(" ");
}

function cancelSearch(): void {
  clearPendingSearchRender();
  if (recoveryTimer !== null) {
    clearTimeout(recoveryTimer);
    recoveryTimer = null;
  }
  recoveryAttempt = 0;
  lastWorkPayload = null;
  awaitingRecoveryChoice = false;
  if (!searching && !intuitionPending) return;
  const wasSearching = searching;
  const wasPreparingModel = modelRuntime.classList.contains("is-active");
  recoveryLog("request-cancelled", {
    requestId,
    workerGeneration,
    reason: "user-or-state-reset",
  });
  searchWorker.postMessage({ type: "cancel", requestId });
  searching = false;
  searchPurpose = null;
  intuitionPending = false;
  activeSearchProgress = null;
  requestId += 1;
  if (wasSearching) {
    document.querySelector("#search-state")!.textContent = "Aborted";
    gameNotice = {
      label: "Search aborted",
      text: `${shortPlayerName(environment.currentPlayer(state))} model turn paused`,
    };
  }
  if (wasPreparingModel) {
    setModelRuntimeMessage(
      "Model preparation stopped",
      "The current request was cancelled. Cached files remain available.",
    );
  }
  renderBoard();
}

function clearIntuitionReadout(message = "Evaluating current position…"): void {
  document.querySelector("#value")!.textContent = "…";
  document.querySelector("#wdl-logits")!.textContent = "Raw logits —";
  for (const row of document.querySelectorAll<HTMLElement>(".wdl-row")) {
    row.querySelector<HTMLElement>("i")!.style.width = "0%";
    row.querySelector<HTMLElement>("strong")!.textContent = "—";
  }
  document.querySelector("#policy-list")!.innerHTML =
    `<p class="empty-copy">${message}</p>`;
}

function beginModelPreparation(force = false): void {
  if (!force && modelRuntime.dataset.phase === "ready") return;
  setModelRuntimeMessage(
    "Starting local model",
    "Checking whether this checkpoint is already cached…",
    { active: true },
  );
}

function backendLabel(backend: Backend): string {
  return backend === "webgpu" ? "WebGPU" : "WASM";
}

function showPreparedModel(): void {
  const backend = activeBackend ?? compatibilityBackend;
  if (backend === "wasm") {
    setModelRuntimeMessage(
      "Model ready · WASM",
      "Running on CPU (Wasm) · slower, but stable.",
      { progress: 1, state: "ready" },
    );
    return;
  }
  if (modelRuntime.dataset.phase === "pending") {
    setModelRuntimeMessage(
      backend ? `Model ready · ${backendLabel(backend)}` : "Model ready",
      "Already prepared for this browser session.",
      { progress: 1, state: "ready" },
    );
  }
}

function postWorkRequest(payload: WorkPayload): void {
  requestId += 1;
  lastWorkPayload = payload;
  const shouldInjectFault = compatibilityBackend === null && e2eFaultBudget > 0;
  if (shouldInjectFault) e2eFaultBudget -= 1;
  recoveryLog("request-dispatched", {
    requestId,
    workerGeneration,
    requestType: payload.type,
    gameId: payload.gameId,
    variantId: payload.variantId,
    modelId: payload.modelId,
    backend: compatibilityBackend ?? "auto-webgpu-preferred",
    recoveryAttempt,
    injectedFault: shouldInjectFault,
    remainingInjectedFaults: e2eFaultBudget,
  });
  searchWorker.postMessage({
    ...payload,
    requestId,
    ...(shouldInjectFault
      ? { fault: { kind: "private-member" as const } }
      : {}),
    ...(compatibilityBackend ? { backendOverride: compatibilityBackend } : {}),
  });
}

function beginFreshWorkRequest(): void {
  recoveryAttempt = 0;
  lastWorkPayload = null;
  awaitingRecoveryChoice = false;
  if (recoveryTimer !== null) {
    clearTimeout(recoveryTimer);
    recoveryTimer = null;
  }
}

function requestPositionIntuition(): void {
  if (!gameStarted || searching || !selectedModelId) return;
  if (!confirmModelDownload()) {
    gameNotice = {
      label: "Model loading cancelled",
      text: selectedDefinition.statusText(state),
    };
    renderGameBanner();
    setModelRuntimeMessage(
      "Model load cancelled",
      "No model weights were downloaded.",
    );
    return;
  }
  beginModelPreparation();
  beginFreshWorkRequest();
  intuitionPending = true;
  document.querySelector("#search-state")!.textContent = "Not searched";
  document.querySelector("#search-time")!.textContent = "—";
  document.querySelector("#search-pv")!.textContent = "—";
  document.querySelector("#mcts-root-value")!.textContent = "—";
  clearIntuitionReadout();
  renderBoard();
  postWorkRequest({
    type: "intuition",
    gameId: selectedGameId,
    variantId: selectedVariantId,
    state,
    modelId: selectedModelId,
  });
}

function commitMove(action: number, source: "human" | "model" = "human"): void {
  // Worker results are asynchronous. Never allow a late result to overwrite a
  // terminal position, and validate against the current (not requested) state
  // before committing it.
  if (environment.isTerminal(state)) return;
  const legalActionMask = environment.legalActionMask(state);
  if (!Number.isInteger(action) || legalActionMask[action] !== 1) {
    gameNotice = {
      label: source === "model" ? "Model move discarded" : "Move unavailable",
      text: source === "model"
        ? "The search result no longer matches the current position. Start the search again."
        : "That action is not legal in the current position.",
    };
    document.querySelector("#search-state")!.textContent = "Result discarded";
    renderBoard();
    return;
  }
  const actingPlayer = environment.currentPlayer(state);
  const label = environment.formatAction(state, action);
  state = environment.step(state, action);
  states.push(state);
  lastMove = { action, player: actingPlayer, label, source };
  gameNotice = null;
  if (source === "model") lastModelAction = { action, player: actingPlayer };
  renderBoard();
  if (environment.isTerminal(state)) return;
  if (isModelTurn()) {
    void startModelTurn();
  } else {
    requestPositionIntuition();
  }
}

async function startSearch(purpose: "move" | "analyze"): Promise<void> {
  if (!gameStarted || searching || environment.isTerminal(state)) return;
  const currentPlayer = environment.currentPlayer(state);
  if (purpose === "move" && !isModelTurn(currentPlayer)) return;
  if (purpose === "analyze" && !isHumanTurn(currentPlayer)) return;
  if (!confirmModelDownload()) {
    document.querySelector("#search-state")!.textContent = "Model load cancelled";
    gameNotice = {
      label: "Model loading cancelled",
      text: selectedDefinition.statusText(state),
    };
    renderGameBanner();
    setModelRuntimeMessage(
      "Model load cancelled",
      "No model weights were downloaded.",
    );
    return;
  }
  beginModelPreparation();
  beginFreshWorkRequest();
  searchHistory = [];
  searching = true;
  intuitionPending = false;
  searchPurpose = purpose;
  gameNotice = null;
  const simulations = simulationCount(setupSimulations);
  activeSearchProgress = { completed: 0, total: simulations };
  document.querySelector("#search-state")!.textContent =
    `${purpose === "analyze" ? "Analyze" : "Move"} · 0 / ${simulations}`;
  renderBoard();
  postWorkRequest({
    type: "search",
    gameId: selectedGameId,
    variantId: selectedVariantId,
    state,
    modelId: selectedModelId,
    simulations,
    mctsConfig: {
      visitExponent: mctsConfig.mcts_visit_exponent,
      maxVisitInit: mctsConfig.mcts_maxvisit_init,
      valueScale: mctsConfig.mcts_value_scale,
      visitAggregator: mctsConfig.mcts_visit_aggregator,
    },
  });
}

function startModelTurn(): Promise<void> {
  return startSearch("move");
}

function pvText(actions: number[]): string {
  const moves: string[] = [];
  let position = state;
  for (const action of actions.slice(0, 6)) {
    moves.push(environment.formatAction(position, action));
    position = environment.step(position, action);
  }
  return moves.join(" ") || "—";
}

function applySearchSnapshot(snapshot: SearchSnapshot): void {
  activeSearchProgress = {
    completed: snapshot.completed,
    total: snapshot.total,
  };
  const label = searchPurpose === "analyze" ? "Analyze" : "Move";
  document.querySelector("#search-state")!.textContent =
    `${label} · ${snapshot.completed} / ${snapshot.total}`;
  document.querySelector("#search-time")!.textContent = formatMs(snapshot.elapsedMs);
  document.querySelector("#search-pv")!.textContent = pvText(snapshot.principalVariation);
  renderSearchThinking(snapshot);
  renderGameBanner();
}

function queueSearchSnapshot(snapshot: SearchSnapshot): void {
  pendingSearchSnapshot = snapshot;
  if (searchRenderFrame !== null) return;
  searchRenderFrame = requestAnimationFrame(() => {
    searchRenderFrame = null;
    const latest = pendingSearchSnapshot;
    pendingSearchSnapshot = null;
    if (latest && searching) applySearchSnapshot(latest);
  });
}

function clearPendingSearchRender(): void {
  pendingSearchSnapshot = null;
  if (searchRenderFrame === null) return;
  cancelAnimationFrame(searchRenderFrame);
  searchRenderFrame = null;
}

function resetRecoveryAfterSuccess(
  responseType: "intuition" | "result",
  completedRequestId: number,
): void {
  recoveryLog("request-succeeded", {
    requestId: completedRequestId,
    workerGeneration,
    responseType,
    backend: compatibilityBackend ?? "webgpu-or-auto",
    recovered: recoveryAttempt > 0,
  });
  recoveryAttempt = 0;
  lastWorkPayload = null;
  awaitingRecoveryChoice = false;
  if (recoveryTimer !== null) {
    clearTimeout(recoveryTimer);
    recoveryTimer = null;
  }
  if (gameNotice?.label === "Recovering") gameNotice = null;
}

function recoverPrivateMemberFailure(): boolean {
  if (!lastWorkPayload) return false;
  const payload = lastWorkPayload;
  const step = nextRecoveryStep(recoveryAttempt);
  const failedAttempt = recoveryAttempt;
  recoveryAttempt += 1;
  if (step.action === "give-up") return false;

  recoveryLog("recovery-step-selected", {
    requestId,
    workerGeneration,
    failedAttempt,
    action: step.action,
    delayMs: "delayMs" in step ? step.delayMs : 0,
    requestType: payload.type,
  });
  searchWorker.postMessage({ type: "cancel", requestId });
  recoveryLog("recovery-cancel-sent", { requestId, workerGeneration });

  clearPendingSearchRender();
  activeSearchProgress = null;
  if (step.action === "offer-choice") {
    awaitingRecoveryChoice = true;
    gameNotice = {
      label: "WebGPU recovery paused",
      text: "Retry WebGPU, or use slower CPU compatibility mode.",
    };
    document.querySelector("#search-state")!.textContent = "Waiting for recovery choice";
    recoveryLog("recovery-choice-shown", {
      requestId,
      workerGeneration,
      requestType: payload.type,
    });
    renderBoard();
    return true;
  }

  awaitingRecoveryChoice = false;
  gameNotice = {
    label: "Recovering",
    text: step.action === "retry-same-worker"
      ? "Letting Safari finish GPU cleanup, then trying WebGPU again…"
      : "Restarting the local inference worker, then trying WebGPU again…",
  };
  document.querySelector("#search-state")!.textContent = "Recovering";
  renderBoard();

  recoveryTimer = setTimeout(() => {
    recoveryTimer = null;
    recoveryLog("recovery-delay-finished", {
      action: step.action,
      workerGeneration,
      requestType: payload.type,
    });
    if (step.action === "recreate-worker") {
      recreateSearchWorker("automatic-webgpu-recovery");
      beginModelPreparation(true);
    }
    postWorkRequest(payload);
  }, step.delayMs);
  return true;
}

function continueRecoveryWithBackend(backend: "webgpu" | "wasm"): void {
  if (!lastWorkPayload || !awaitingRecoveryChoice) return;
  const payload = lastWorkPayload;
  awaitingRecoveryChoice = false;
  recoveryAttempt = 0;
  compatibilityBackend = backend === "wasm" ? "wasm" : null;
  activeBackend = null;
  recoveryLog("recovery-choice-selected", {
    requestId,
    workerGeneration,
    choice: backend,
    requestType: payload.type,
  });
  gameNotice = {
    label: "Recovering",
    text: backend === "wasm"
      ? "Starting CPU compatibility mode…"
      : "Starting a fresh WebGPU worker…",
  };
  document.querySelector("#search-state")!.textContent = "Recovering";
  recreateSearchWorker(
    backend === "wasm" ? "user-selected-cpu" : "user-retried-webgpu",
  );
  beginModelPreparation(true);
  renderBoard();
  postWorkRequest(payload);
}

function handleWorkerMessage(event: MessageEvent<SearchWorkerMessage>): void {
  const message = event.data;
  if (message.requestId !== requestId) return;
  if (message.type === "progress" && message.progress) {
    setProgress(message.progress);
    if (!intuitionPending) {
      const label = searchPurpose === "analyze" ? "Analyze" : "Move";
      document.querySelector("#search-state")!.textContent =
        `${label} · Loading ${message.progress.phase}`;
    }
  } else if (message.type === "intuition" && message.intuition) {
    resetRecoveryAfterSuccess("intuition", message.requestId);
    intuitionPending = false;
    showPreparedModel();
    renderModelIntuition(message.intuition);
    renderBoard();
  } else if (message.type === "search-progress" && message.snapshot) {
    searchHistory.push(message.snapshot);
    if (message.snapshot.completed === 0) renderModelIntuition(message.snapshot);
    queueSearchSnapshot(message.snapshot);
  } else if (message.type === "result" && message.result) {
    if (!searching || searchPurpose === null) {
      recoveryLog("stale-result-ignored", {
        requestId: message.requestId,
        workerGeneration,
      });
      return;
    }
    resetRecoveryAfterSuccess("result", message.requestId);
    clearPendingSearchRender();
    showPreparedModel();
    const completedPurpose = searchPurpose;
    searching = false;
    searchPurpose = null;
    activeSearchProgress = null;
    document.querySelector("#search-state")!.textContent =
      completedPurpose === "analyze" ? "Analysis complete" : "Complete";
    document.querySelector("#search-time")!.textContent = formatMs(message.result.elapsedMs);
    document.querySelector("#search-pv")!.textContent = pvText(message.result.principalVariation);
    renderModelIntuition(message.result);
    renderSearchThinking(message.result);
    if (completedPurpose === "analyze") {
      const bestAction = environment.formatAction(state, message.result.action);
      gameNotice = {
        label: "Analysis complete",
        text: `Best action · ${bestAction}`,
      };
      renderBoard();
    } else {
      commitMove(message.result.action, "model");
    }
  } else if (message.type === "error") {
    const failedIntuition = intuitionPending && !searching;
    console.error("search worker request failed", {
      name: message.name,
      message: message.message,
    });
    for (const [index, cause] of (message.causeChain ?? []).entries()) {
      console.error(`search worker cause ${index}`, cause);
    }
    recoveryLog("request-failed", {
      requestId: message.requestId,
      workerGeneration,
      requestType: lastWorkPayload?.type ?? "unknown",
      name: message.name,
      message: message.message,
      modelReady: message.modelReady,
      backend: compatibilityBackend ?? "webgpu-or-auto",
      recoveryAttempt,
      causeChain: message.causeChain ?? [],
    });
    const privateMemberFailure = isPrivateMemberAccessError(message.message ?? "");
    if (privateMemberFailure && recoverPrivateMemberFailure()) return;
    const safariPrivateFailure = isSafariBrowser(navigator.userAgent) &&
      privateMemberFailure;
    searching = false;
    searchPurpose = null;
    intuitionPending = false;
    activeSearchProgress = null;
    if (!failedIntuition) {
      document.querySelector("#search-state")!.textContent =
        message.name === "AbortError" ? "Aborted" : "Error";
    }
    // A follow-up intuition failure must not hide a successfully committed
    // model move—especially Pass, which has no board location to highlight.
    gameNotice = safariPrivateFailure
      ? {
          label: "Automatic recovery failed",
          text: "Press Restart or Search position to retry manually.",
        }
      : failedIntuition && lastMove?.source === "model"
        ? null
        : {
            label: failedIntuition ? "Position intuition unavailable" : "Search failed",
            text: message.message ?? "Inference failed",
          };
    renderBoard();
    if (message.modelReady && message.name !== "AbortError") {
      setModelRuntimeMessage(
        activeBackend
          ? `Model ready · ${backendLabel(activeBackend)}`
          : "Model ready",
        "The local model remains loaded; the last evaluation could not be completed.",
        { progress: 1, state: "ready" },
      );
    } else {
      setModelRuntimeMessage(
        message.name === "AbortError" ? "Model preparation stopped" : "Model unavailable",
        message.message ?? "The model could not be prepared in this browser.",
      );
    }
  }
}

function setGameStarted(started: boolean): void {
  gameStarted = started;
  document.body.dataset.gamePhase = started ? "playing" : "setup";
  playButton.textContent = started ? "Restart" : "Play";
}

function resetSearchReadout(): void {
  searchHistory = [];
  intuitionPending = false;
  activeSearchProgress = null;
  document.querySelector("#search-state")!.textContent = "Idle";
  document.querySelector("#search-time")!.textContent = "—";
  document.querySelector("#search-pv")!.textContent = "—";
  document.querySelector("#value")!.textContent = "—";
  document.querySelector("#mcts-root-value")!.textContent = "—";
  document.querySelector("#wdl-logits")!.textContent = "Raw logits —";
  for (const row of document.querySelectorAll<HTMLElement>(".wdl-row")) {
    row.querySelector<HTMLElement>("i")!.style.width = "0%";
    row.querySelector<HTMLElement>("strong")!.textContent = "—";
  }
  document.querySelector("#policy-list")!.innerHTML =
    '<p class="empty-copy">Policy appears when play begins.</p>';
}

function startGame(): void {
  if (!confirmModelDownload()) {
    setModelRuntimeMessage(
      "Model load cancelled",
      "No model weights were downloaded.",
    );
    return;
  }
  beginModelPreparation();
  cancelSearch();
  humanPlayer = Number(humanPlayerElement.value) as Player;
  state = environment.init();
  states = [state];
  lastModelAction = null;
  lastMove = null;
  gameNotice = null;
  resetSearchReadout();
  setGameStarted(true);
  renderBoard();
  if (window.matchMedia("(max-width: 820px)").matches) {
    requestAnimationFrame(() => boardColumn.scrollIntoView({ block: "start" }));
  }
  if (isModelTurn()) {
    void startModelTurn();
  } else {
    requestPositionIntuition();
  }
}

setupSimulations.addEventListener("input", () => {
  renderSimulationCount(
    setupSimulations,
    document.querySelector<HTMLOutputElement>("#setup-simulations-value")!,
  );
});
setupSimulations.addEventListener("keydown", adjustSimulationWithKeyboard);
analyzeButton.addEventListener("click", () =>
  void startSearch(
    isHumanTurn() ? "analyze" : "move",
  )
);
playButton.addEventListener("click", startGame);
selfPlayButton.addEventListener("click", () => {
  cancelSearch();
  selfPlayMode = !selfPlayMode;
  renderSelfPlayMode();
  renderBoard();
  if (!gameStarted || environment.isTerminal(state)) return;
  if (isModelTurn()) {
    void startModelTurn();
  } else {
    requestPositionIntuition();
  }
});

document.querySelector("#new-game")!.addEventListener("click", () => {
  cancelSearch();
  state = environment.init();
  states = [state];
  lastModelAction = null;
  lastMove = null;
  gameNotice = null;
  resetSearchReadout();
  setGameStarted(false);
  renderBoard();
});
undoButton.addEventListener("click", () => {
  cancelSearch();
  if (states.length <= 1) return;
  states.pop();
  // Undo the model reply too when possible, returning control to the human.
  if (
    !selfPlayMode &&
    states.length > 1 &&
    environment.currentPlayer(states[states.length - 1]) !== humanPlayer
  ) {
    states.pop();
  }
  state = states[states.length - 1]!;
  lastModelAction = null;
  lastMove = null;
  gameNotice = null;
  renderBoard();
  if (!environment.isTerminal(state) && isModelTurn()) {
    void startModelTurn();
  } else {
    requestPositionIntuition();
  }
});
passButton.addEventListener("click", () => {
  if (!gameStarted || searching || environment.isTerminal(state) ||
      !isHumanTurn()) return;
  commitMove(selectedDefinition.actionCount - 1);
});
copyGameRecordButton.addEventListener("click", async () => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(gameRecord.value);
    } else {
      gameRecord.focus();
      gameRecord.select();
      if (!document.execCommand("copy")) throw new Error("Copy command unavailable");
      gameRecord.setSelectionRange(0, 0);
      gameRecord.blur();
    }
    copyGameRecordStatus.textContent = "Copied to clipboard";
  } catch {
    copyGameRecordStatus.textContent = "Copy failed · select the record manually";
    gameRecord.focus();
    gameRecord.select();
  }
});
cancelButton.addEventListener("click", cancelSearch);
retryWebgpuButton.addEventListener("click", () => {
  continueRecoveryWithBackend("webgpu");
});
useCpuModeButton.addEventListener("click", () => {
  continueRecoveryWithBackend("wasm");
});
document.querySelector("#load-pgn")!.addEventListener("click", () => {
  const pgn = document.querySelector<HTMLTextAreaElement>("#pgn-input")!.value;
  cancelSearch();
  try {
    humanPlayer = Number(humanPlayerElement.value) as Player;
    if (!selectedDefinition.loadGame) {
      throw new Error(`${selectedDefinition.gameName} has no game loader`);
    }
    states = [...selectedDefinition.loadGame(pgn)];
    if (states.length === 0) throw new Error("PGN contains no position");
    state = states[states.length - 1]!;
    lastModelAction = null;
    lastMove = null;
    gameNotice = null;
    resetSearchReadout();
    setGameStarted(true);
    renderBoard();
    if (!environment.isTerminal(state) && isModelTurn()) {
      void startModelTurn();
    } else {
      requestPositionIntuition();
    }
  } catch (error) {
    gameNotice = {
      label: "Position could not be loaded",
      text: error instanceof Error ? error.message : "Invalid PGN",
    };
    renderGameBanner();
  }
});
humanPlayerElement.addEventListener("change", () => {
  humanPlayer = Number(humanPlayerElement.value) as Player;
  viewPlayer = humanPlayer;
  boardViewElement.value = String(viewPlayer);
  renderBoard();
});
boardViewElement.addEventListener("change", () => {
  viewPlayer = Number(boardViewElement.value) as Player;
  renderBoard();
});
modelSelect.addEventListener("change", () => {
  selectedModelId = modelSelect.value;
  updateSelectedModelSummary();
  renderUnbeatableNotice();
  compatibilityBackend = null;
  activeBackend = null;
  searchWorker.postMessage({ type: "dispose" });
  cancelSearch();
  if (!gameStarted) return;
  if (!environment.isTerminal(state) && isModelTurn()) {
    void startModelTurn();
  } else {
    requestPositionIntuition();
  }
});

function renderModelOptions(preferredModelId?: string): void {
  const compatible = selectedDefinition.modelIds.flatMap((modelId) => {
    const model = availableModels.find((entry) => entry.id === modelId);
    return model ? [model] : [];
  });
  if (compatible.length === 0) {
    modelSelect.innerHTML = '<option value="">No compatible model</option>';
    selectedModelId = "";
    updateSelectedModelSummary();
    return;
  }
  const preferred = compatible.find((model) => model.id === preferredModelId)
    ?? compatible[0]!;
  selectedModelId = preferred.id;
  modelSelect.innerHTML = compatible.map((entry) =>
    `<option value="${entry.id}" ${entry.id === selectedModelId ? "selected" : ""}>${entry.displayName}</option>`
  ).join("");
  updateSelectedModelSummary();
}

function updateSelectedModelSummary(): void {
  const selected = availableModels.find((model) => model.id === selectedModelId);
  if (!selected) {
    setModelRuntimeMessage(
      "Model unavailable",
      "Choose another game or variant with a compatible model.",
    );
    return;
  }
  const trained = selected.trained !== false;
  const size = selected.downloadBytes > 0
    ? `First use up to ${formatBytes(selected.downloadBytes)} · saved locally for later visits.`
    : "Model weights load locally when play begins.";
  setModelRuntimeMessage(
    trained ? "Ready to load" : "Untrained model",
    size,
  );
}

function renderVariantOptions(): void {
  const variants = gameRegistry.variants(selectedGameId);
  variantSelect.innerHTML = variants.map((definition) =>
    `<option value="${definition.variantId}" ${definition.variantId === selectedVariantId ? "selected" : ""}>${definition.variantName}</option>`
  ).join("");
}

function applySelectedVariant(): void {
  cancelSearch();
  compatibilityBackend = null;
  activeBackend = null;
  searchWorker.postMessage({ type: "dispose" });
  renderer.dispose();
  selectedDefinition = gameRegistry.get(selectedGameId, selectedVariantId);
  environment = selectedDefinition.createEnvironment();
  renderer = selectedDefinition.createRenderer();
  renderer.mount(boardElement);
  state = environment.init();
  states = [state];
  lastModelAction = null;
  lastMove = null;
  gameNotice = null;
  humanPlayerElement.innerHTML = selectedDefinition.playerNames.map(
    (name, player) =>
      `<option value="${player}">P${player + 1} · ${name}</option>`,
  ).join("");
  humanPlayerElement.value = String(selectedDefinition.defaultHumanPlayer);
  humanPlayer = Number(humanPlayerElement.value) as Player;
  viewPlayer = humanPlayer;
  boardViewElement.value = String(viewPlayer);
  boardViewControl.hidden = selectedGameId !== "chess";
  renderSelfPlayMode();
  setupSimulations.value = String(selectedDefinition.defaultSimulations);
  renderSimulationCount(
    setupSimulations,
    document.querySelector<HTMLOutputElement>("#setup-simulations-value")!,
  );
  renderAdvancedTools();
  passButton.hidden = !selectedDefinition.controls.pass;
  boardContext.textContent =
    `${selectedDefinition.gameName} · ${selectedDefinition.variantName}`;
  renderModelOptions(selectedModelId);
  resetSearchReadout();
  setGameStarted(false);
  renderBoard();
}

gameSelect.addEventListener("change", () => {
  selectedGameId = gameSelect.value;
  const firstVariant = gameRegistry.variants(selectedGameId)[0];
  if (!firstVariant) throw new Error(`Game ${selectedGameId} has no variants`);
  selectedVariantId = firstVariant.variantId;
  renderVariantOptions();
  applySelectedVariant();
});
variantSelect.addEventListener("change", () => {
  selectedVariantId = variantSelect.value;
  applySelectedVariant();
});
window.addEventListener("beforeunload", () => {
  renderer.dispose();
  searchWorker.postMessage({ type: "dispose" });
});

async function initialize(): Promise<void> {
  gameSelect.innerHTML = gameRegistry.games().map((game) =>
    `<option value="${game.id}" ${game.id === selectedGameId ? "selected" : ""}>${game.name}</option>`
  ).join("");
  renderVariantOptions();
  try {
    const index = await listModels("/models");
    availableModels = index.models;
    for (const entry of index.models) modelDownloadBytes.set(entry.id, entry.downloadBytes);
    renderModelOptions(index.defaultModel);
  } catch (error) {
    availableModels = [{
      id: "helpful-bush-570-v6",
      displayName: "Helpful Bush 570 · v6 (Easy)",
      manifest: "helpful-bush-570-v6/manifest.json",
      downloadBytes: 0,
    }];
    renderModelOptions("helpful-bush-570-v6");
    console.warn("Could not read model index", error);
  }
  applySelectedVariant();
  document.body.dataset.ready = "true";
}

void initialize();
